/**
 * CourseworkTranscript — Printable "official transcript" style report of
 * everything a learner has completed. Rendered for the current user only.
 * Designed to be printed to PDF or handed to an employer.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Printer, ShieldCheck, GraduationCap, CheckCircle2, Circle,
  Loader2, ExternalLink,
} from 'lucide-react';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (_) { return iso; }
};

const STATUS_LABEL = {
  completed: 'Certificate earned',
  audit_complete: 'Coursework complete (no cert)',
  in_progress: 'In progress',
};

const CourseworkTranscript = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = window.localStorage.getItem('token');
        const res = await axios.get(`${API}/lms/my-transcript`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setData(res.data);
      } finally { setLoading(false); }
    })();
  }, [API, user]);

  return (
    <div className="min-h-screen bg-muted print:bg-white" data-testid="transcript-page">
      {/* Toolbar */}
      <div className="print:hidden bg-card border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="transcript-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/my-certificates')}
              className="rounded-full font-bold"
              data-testid="transcript-certs-btn"
            >
              <GraduationCap className="w-4 h-4 mr-1.5" /> My certificates
            </Button>
            <Button
              onClick={() => window.print()}
              className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="transcript-print-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-24 text-foreground/60" data-testid="transcript-loading">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
          Loading transcript…
        </div>
      )}

      {!loading && data && (
        <main className="container mx-auto px-6 py-10 print:py-6 max-w-4xl">
          {/* Header block — becomes the letterhead when printed */}
          <div className="bg-card rounded-3xl print:rounded-none shadow-xl print:shadow-none border border-border print:border-b-2 print:border-navy p-8 print:p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-primary">Official transcript</div>
                    <h1 className="font-heading text-2xl font-extrabold text-foreground tracking-tight">Freeze Dry Academy</h1>
                  </div>
                </div>
                <p className="mt-4 text-xs text-foreground/60">Generated {fmt(data.generated_at)}</p>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60">Learner</div>
                <div className="font-heading text-xl font-extrabold text-foreground tracking-tight" data-testid="transcript-learner-name">
                  {data.learner.name}
                </div>
                <div className="text-xs text-foreground/60 mt-0.5">{data.learner.email}</div>
                <div className="text-xs text-foreground/60 font-mono mt-0.5">ID {data.learner.user_id}</div>
              </div>
            </div>

            {/* Summary strip */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                ['Courses touched', data.summary.courses_touched],
                ['Courses completed', data.summary.courses_completed],
                ['Certificates', data.summary.certificates_earned],
                ['Lessons completed', data.summary.lessons_completed],
                ['Hours logged', data.summary.total_hours],
              ].map(([label, val], i) => (
                <div
                  key={label}
                  className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-center"
                  data-testid={`transcript-summary-${i}`}
                >
                  <div className="font-heading text-3xl font-extrabold text-primary tracking-tight">{val}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60 mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Courses table */}
          <div className="mt-6 bg-card rounded-3xl print:rounded-none shadow-xl print:shadow-none border border-border p-8 print:p-6">
            <h2 className="font-heading text-xl font-extrabold text-foreground tracking-tight mb-6">
              Coursework detail
            </h2>

            {data.courses.length === 0 && (
              <div className="text-center text-foreground/60 py-10" data-testid="transcript-empty">
                No coursework recorded yet. Start any course in the Learning library and it will appear here.
              </div>
            )}

            <div className="space-y-6">
              {data.courses.map((c) => (
                <div
                  key={c.course_id}
                  className="border-b border-border last:border-b-0 pb-5 last:pb-0"
                  data-testid={`transcript-course-${c.course_id}`}
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60">
                        {c.tier.replace('_', ' ')} · Instructor: {c.instructor_name}
                      </div>
                      <h3 className="font-heading text-lg font-extrabold text-foreground tracking-tight mt-0.5">
                        {c.title}
                      </h3>
                    </div>
                    <div className={
                      c.status === 'completed'
                        ? 'inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/40 px-3 py-1 text-xs font-extrabold'
                        : c.status === 'audit_complete'
                        ? 'inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary border border-primary/40 px-3 py-1 text-xs font-extrabold'
                        : 'inline-flex items-center gap-1.5 rounded-full bg-muted text-foreground/70 border border-border px-3 py-1 text-xs font-extrabold'
                    }>
                      {c.status === 'completed' ? <ShieldCheck className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                      {STATUS_LABEL[c.status] || c.status}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-bold text-foreground/70">
                        {c.completed_lessons} of {c.total_lessons} lessons · {c.verified_minutes} verified minutes
                      </span>
                      <span className="font-heading font-extrabold text-foreground">{c.percent_complete}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-primary/10 overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${c.percent_complete}%` }} />
                    </div>
                  </div>

                  {/* Module breakdown */}
                  {c.modules.length > 0 && (
                    <ul className="mt-4 space-y-1.5">
                      {c.modules.map((m) => (
                        <li
                          key={m.id}
                          className="flex items-center gap-2 text-sm"
                          data-testid={`transcript-module-${m.id}`}
                        >
                          {m.completed ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 text-foreground/30 flex-shrink-0" />
                          )}
                          <span className={m.completed ? 'font-bold text-foreground' : 'text-foreground/70'}>
                            {m.title}
                          </span>
                          <span className="text-xs text-foreground/50 ml-auto">
                            {m.completed_lessons}/{m.total_lessons}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {c.certificate && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-foreground/70">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                      Certificate{' '}
                      <span className="font-mono font-extrabold text-foreground">{c.certificate.cert_number}</span>
                      {' '}issued {fmt(c.certificate.issued_at)}. Verify at{' '}
                      <span className="font-mono text-primary">/certificate/{c.certificate.cert_number}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer / verification note (visible in print) */}
          <div className="mt-6 pro-card p-5 flex items-start gap-4 text-sm text-foreground/70 print:rounded-none print:border-t-2 print:border-navy print:bg-transparent print:shadow-none" data-testid="transcript-footer">
            <ExternalLink className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              This transcript is a self-service report generated from the learner&apos;s own progress record. Any listed
              certificate can be independently verified — with signature and revoke status — at{' '}
              <span className="font-mono text-primary">freezedryacademy.com/certificate/&lt;certificate-number&gt;</span>. For an
              official signed transcript, contact{' '}
              <span className="font-mono text-primary">registrar@freezedryacademy.com</span>.
            </div>
          </div>
        </main>
      )}

      {/* Print-only tweaks: expand backgrounds, hide toolbars */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          .pro-card { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
};

export default CourseworkTranscript;
